import { useCallback, useEffect, useRef, useState } from 'react';
import type { DragOverEvent } from '@dnd-kit/core';
import { dayIdFromDrop } from './dayDropTarget';

/**
 * How long the pointer has to rest on a day chip before a drop there counts.
 * Users kept moving stops to days they never aimed at: the bar floats over the
 * list, and a card dragged down to be reordered crossed a chip on the way and
 * landed in it. A pause is intent; passing through is not.
 */
export const ARM_DELAY_MS = 400;

/**
 * The day chip the pointer is resting on (`hoverDayId`) and, once it has rested
 * long enough, the one a drop would move the stop to (`armedDayId`).
 */
export function useArmedDayDrop() {
  const [hoverDayId, setHoverDayId] = useState<string | null>(null);
  const [armedDayId, setArmedDayId] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  const onDragOver = useCallback(({ over }: DragOverEvent) => {
    const dayId = over ? dayIdFromDrop(String(over.id)) : null;
    clearTimer();
    setHoverDayId(dayId);
    setArmedDayId(null);
    if (dayId) timer.current = setTimeout(() => setArmedDayId(dayId), ARM_DELAY_MS);
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    setHoverDayId(null);
    setArmedDayId(null);
  }, []);

  useEffect(() => clearTimer, []);

  return { hoverDayId, armedDayId, onDragOver, reset };
}
